import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { isToday, isYesterday } from 'date-fns';
import {
  ArchiveIcon,
  ChevronDownIcon,
  GlobeIcon,
  HistoryIcon,
  MessageCircleIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PinIcon,
  RotateCcwIcon,
  Share2Icon,
  TrashIcon,
  UsersIcon,
} from 'lucide-react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
} from 'react';

import { Button } from '@/components/ui/button.js';
import { AppDialog } from '@/components/ui/app-dialog.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  archiveConversation,
  deleteConversation,
  fetchConversations,
  restoreConversation,
  updateConversation,
} from '@/integration/services/integration-api.js';
import { useDedicatedAutomationConversationIds } from '@/lib/api/automations.js';
import { archiveWorkspaceArtifact } from '@/lib/api/workspace-artifacts.js';
import { recentsKey } from '@/lib/api/recents-key.js';
import { ConversationShareDialog } from '@/features/chat-share/ConversationShareDialog.js';
import type { ConversationVisibility } from '@/features/chat-share/conversation-audience.js';
import { useUiStore } from '@/features/settings/ui-store.js';
import type { Conversation } from '@/integration/types.js';
import { notify } from '@/lib/notify.js';
import { cn } from '@/lib/utils.js';

import { DeleteChatDialog } from './DeleteChatDialog.js';

import {
  sidebarRowStretchClass,
  sidebarUnreadDotClass,
} from '../../app/sidebar-primitives.js';

const PAGE_SIZE = 50;

/**
 * The sidebar lanes, in render order. Pinned chats lead whatever their visibility, then the shared
 * lanes (§11) so collaboration isn't buried under a long private history, and the caller's own
 * threads close it out under Recents. Each lane's header carries its glyph, so a row doesn't repeat
 * it. A chat with no `visibility` (rolling-deploy window) counts as private; a personal workspace
 * only ever fills Pinned and Recents.
 */
const LANES = [
  { key: 'pinned', label: 'Pinned', Icon: PinIcon, bucketByDate: false },
  { key: 'group', label: 'Group', Icon: UsersIcon, bucketByDate: false },
  { key: 'public', label: 'Public', Icon: GlobeIcon, bucketByDate: false },
  { key: 'private', label: 'Recents', Icon: HistoryIcon, bucketByDate: true },
] as const;

type LaneKey = (typeof LANES)[number]['key'];

/** File the loaded conversations into the lanes above — pinned first, then by visibility. */
function partitionIntoLanes(
  items: ReadonlyArray<Conversation>,
): Record<LaneKey, Conversation[]> {
  const lanes: Record<LaneKey, Conversation[]> = {
    pinned: [],
    group: [],
    public: [],
    private: [],
  };
  for (const conversation of items) {
    if (conversation.pinnedAt) {
      lanes.pinned.push(conversation);
      continue;
    }
    const visibility = conversation.visibility ?? 'private';
    lanes[
      visibility === 'group' || visibility === 'public' ? visibility : 'private'
    ].push(conversation);
  }
  return lanes;
}

export function SidebarConversations({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean;
  onNavigate?: () => void;
}): JSX.Element | null {
  const qc = useQueryClient();
  const router = useRouterState();
  const navigate = useNavigate();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const archivedSentinelRef = useRef<HTMLDivElement>(null);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [lifecycle, setLifecycle] = useState<{
    action: 'archive' | 'restore';
    conversation: Conversation;
  } | null>(null);
  const lifecycleInFlightRef = useRef(false);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<Conversation | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const activeId = readSearchParam(router.location.search, 'conversationId');
  const selectedWorkspaceId = useUiStore((s) => s.selectedWorkspaceId);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: recentsKey(selectedWorkspaceId),
      queryFn: ({ pageParam = 0 }) =>
        fetchConversations(undefined, undefined, {
          limit: PAGE_SIZE,
          offset: pageParam as number,
          workspaceId: selectedWorkspaceId,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        if (lastPage.length < PAGE_SIZE) return undefined;
        return allPages.reduce((acc, p) => acc + p.length, 0);
      },
      staleTime: 15_000,
    });

  const archivedQuery = useInfiniteQuery({
    queryKey: ['archived-conversations', selectedWorkspaceId],
    queryFn: ({ pageParam = 0 }) =>
      fetchConversations(undefined, undefined, {
        limit: PAGE_SIZE,
        offset: pageParam as number,
        status: 'archived',
        workspaceId: selectedWorkspaceId,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      return allPages.reduce((count, page) => count + page.length, 0);
    },
    enabled: archivedOpen && selectedWorkspaceId !== null,
    staleTime: 15_000,
  });

  const fetchRef = useRef(fetchNextPage);
  const canFetchRef = useRef(hasNextPage && !isFetchingNextPage);

  useEffect(() => {
    fetchRef.current = fetchNextPage;
    canFetchRef.current = hasNextPage && !isFetchingNextPage;
  });

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && canFetchRef.current) {
          void fetchRef.current();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!archivedOpen) return;
    const element = archivedSentinelRef.current;
    if (!element) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          archivedQuery.hasNextPage &&
          !archivedQuery.isFetchingNextPage
        ) {
          void archivedQuery.fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [
    archivedOpen,
    archivedQuery.fetchNextPage,
    archivedQuery.hasNextPage,
    archivedQuery.isFetchingNextPage,
  ]);

  // Dedicated automation chats live under the Automations section — subtract them here so they don't
  // also show in Recents. Held back by `isReady` (see below) so a shared dedicated chat cannot flash
  // in a lane before it settles. (See useDedicatedAutomationConversationIds for the >50-automations
  // caveat.)
  const { ids: dedicatedConvIds, isReady: dedicatedConvIdsReady } =
    useDedicatedAutomationConversationIds();
  const allItems = useMemo(
    () => (data?.pages.flat() ?? []).filter((c) => !dedicatedConvIds.has(c.id)),
    [data, dedicatedConvIds],
  );
  const lanes = useMemo(() => {
    const partitioned = partitionIntoLanes(allItems);
    return LANES.map((lane) => ({
      ...lane,
      items: partitioned[lane.key],
    })).filter(
      (lane) =>
        lane.items.length > 0 ||
        (allItems.length === 0 && lane.key === 'private'),
    );
  }, [allItems]);

  if (collapsed) return null;

  function go(conversationId: string): void {
    void navigate({ to: '/chats', search: { conversationId } as never });
    onNavigate?.();
  }

  async function pin(c: Conversation): Promise<void> {
    try {
      await updateConversation(c.id, { pinned: !c.pinnedAt });
      void qc.invalidateQueries({ queryKey: ['recents'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    } catch {
      notify.error('Failed to update pin');
    }
  }

  async function rename(c: Conversation, newTitle: string): Promise<void> {
    const trimmed = newTitle.trim();
    if (!trimmed || trimmed === (c.title || 'Untitled')) return;
    try {
      await updateConversation(c.id, { title: trimmed });
      void qc.invalidateQueries({ queryKey: ['recents'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
    } catch {
      notify.error('Failed to rename conversation');
    }
  }

  /**
   * Delete the chat, then archive the files its author chose to take down with it (MEDA-1150). The
   * files go only after the server confirmed the delete, so a refused delete never strips a live chat
   * of its files. Each file goes through the artifacts archive route, which checks the delete matrix
   * again; one that fails stays and is counted in the toast. The dialog stays busy until the files are
   * done, so closing it means the whole request has finished.
   */
  async function remove(c: Conversation, artifactIds: string[]): Promise<void> {
    setDeleteBusy(true);
    try {
      if (!(await deleteConversation(c.id))) throw new Error('not deleted');
      void qc.invalidateQueries({ queryKey: ['recents'] });
      void qc.invalidateQueries({ queryKey: ['archived-conversations'] });
      void qc.invalidateQueries({ queryKey: ['conversations'] });
      if (c.id === activeId) {
        void navigate({ to: '/home' });
      }
      const failedFiles = await archiveChatFiles(c, artifactIds);
      setPendingDelete(null);
      notify.success(`"${c.title || 'Untitled'}" deleted`);
      if (failedFiles > 0) {
        notify.error(
          `Couldn't delete ${failedFiles} file${failedFiles === 1 ? '' : 's'} created in this chat`,
        );
      }
    } catch {
      notify.error('Failed to delete conversation');
    } finally {
      setDeleteBusy(false);
    }
  }

  /** Archive the chosen files of a deleted chat. Answers how many could not be archived. */
  async function archiveChatFiles(
    c: Conversation,
    artifactIds: string[],
  ): Promise<number> {
    const workspaceId = c.workspaceId ?? selectedWorkspaceId;
    if (artifactIds.length === 0 || !workspaceId) return artifactIds.length;
    const results = await Promise.allSettled(
      artifactIds.map((artifactId) =>
        archiveWorkspaceArtifact(workspaceId, artifactId),
      ),
    );
    void qc.invalidateQueries({
      queryKey: ['workspace-artifacts', workspaceId],
    });
    return results.filter((result) => result.status === 'rejected').length;
  }

  function invalidateLifecycleQueries(): void {
    void qc.invalidateQueries({ queryKey: ['recents'] });
    void qc.invalidateQueries({ queryKey: ['archived-conversations'] });
    void qc.invalidateQueries({ queryKey: ['conversations'] });
  }

  async function confirmLifecycle(): Promise<void> {
    if (!lifecycle || lifecycleInFlightRef.current) return;
    lifecycleInFlightRef.current = true;
    setLifecycleBusy(true);
    const { action, conversation } = lifecycle;
    try {
      if (action === 'archive') await archiveConversation(conversation.id);
      else await restoreConversation(conversation.id);
      const sourceKey =
        action === 'archive'
          ? recentsKey(selectedWorkspaceId)
          : ['archived-conversations', selectedWorkspaceId];
      qc.setQueryData<{ pages: Conversation[][]; pageParams: unknown[] }>(
        sourceKey,
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) =>
                  page.filter((row) => row.id !== conversation.id),
                ),
              }
            : old,
      );
      qc.setQueryData<Conversation>(['conversation', conversation.id], (old) =>
        old
          ? {
              ...old,
              status: action === 'archive' ? 'archived' : 'active',
              ...(action === 'restore' ? { archivedAt: null } : {}),
            }
          : old,
      );
      setLifecycle(null);
      invalidateLifecycleQueries();
      void qc.invalidateQueries({
        queryKey: ['conversation', conversation.id],
      });
      if (action === 'archive' && conversation.id === activeId) {
        void navigate({ to: '/home' });
      }
      notify.success(
        action === 'archive'
          ? `"${conversation.title || 'Untitled'}" archived`
          : `"${conversation.title || 'Untitled'}" restored`,
      );
    } catch {
      notify.error(
        action === 'archive'
          ? 'Failed to archive conversation'
          : 'Failed to restore conversation',
      );
    } finally {
      lifecycleInFlightRef.current = false;
      setLifecycleBusy(false);
    }
  }

  function renderActiveLane(lane: (typeof lanes)[number]): JSX.Element {
    return (
      <ConversationLane
        key={lane.key}
        label={lane.label}
        Icon={lane.Icon}
        bucketByDate={lane.bucketByDate}
        items={lane.items}
        emptyMessage={
          lane.items.length > 0
            ? null
            : selectedWorkspaceId
              ? 'No chats in this workspace yet.'
              : 'No chats yet. Start one above.'
        }
        renderRow={(conversation) => (
          <ConversationRow
            key={conversation.id}
            conversation={conversation}
            active={activeId === conversation.id}
            onClick={() => go(conversation.id)}
            onPin={
              conversation.canManage === true
                ? () => void pin(conversation)
                : undefined
            }
            onRename={
              conversation.canManage === true
                ? (title) => void rename(conversation, title)
                : undefined
            }
            onDelete={
              conversation.canManage === true
                ? () => setPendingDelete(conversation)
                : undefined
            }
            onArchive={
              selectedWorkspaceId !== null && conversation.canManage === true
                ? () => setLifecycle({ action: 'archive', conversation })
                : undefined
            }
          />
        )}
      />
    );
  }

  return (
    <div className="sidebar-fade flex min-w-0 flex-col gap-0.5 px-3">
      {(isLoading || !dedicatedConvIdsReady) && <ChatSkeleton />}

      {!isLoading &&
        dedicatedConvIdsReady &&
        lanes.filter((lane) => lane.key === 'pinned').map(renderActiveLane)}

      {selectedWorkspaceId !== null && (
        <ConversationLane
          label="Archived"
          Icon={ArchiveIcon}
          bucketByDate={false}
          items={archivedQuery.data?.pages.flat() ?? []}
          emptyMessage={
            archivedOpen &&
            !archivedQuery.isLoading &&
            !archivedQuery.isError &&
            (archivedQuery.data?.pages.flat().length ?? 0) === 0
              ? 'No archived chats in this workspace.'
              : null
          }
          open={archivedOpen}
          onOpenChange={setArchivedOpen}
          renderRow={(conversation) => (
            <ConversationRow
              key={conversation.id}
              conversation={conversation}
              active={activeId === conversation.id}
              archived
              onClick={() => go(conversation.id)}
              onDelete={
                conversation.canManage === true
                  ? () => setPendingDelete(conversation)
                  : undefined
              }
              onRestore={
                conversation.canManage === true
                  ? () => setLifecycle({ action: 'restore', conversation })
                  : undefined
              }
            />
          )}
        />
      )}
      {archivedOpen && archivedQuery.isLoading && <ChatSkeleton count={2} />}
      {archivedOpen && archivedQuery.isError && (
        <div className="px-[10px] pb-2 text-xs text-sidebar-section/70">
          <p role="alert">Archived chats are unavailable.</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void archivedQuery.refetch()}
          >
            Try again
          </Button>
        </div>
      )}
      <div ref={archivedSentinelRef} className="h-1 shrink-0" />
      {archivedQuery.isFetchingNextPage && <ChatSkeleton count={2} />}

      {!isLoading &&
        dedicatedConvIdsReady &&
        lanes.filter((lane) => lane.key !== 'pinned').map(renderActiveLane)}

      {/* Infinite scroll sentinel — outside the lanes so paging survives a collapsed section. */}
      <div ref={sentinelRef} className="h-1 shrink-0" />
      {isFetchingNextPage && <ChatSkeleton count={2} />}

      {pendingDelete && (
        <DeleteChatDialog
          conversationId={pendingDelete.id}
          title={pendingDelete.title || 'Untitled'}
          busy={deleteBusy}
          onConfirm={(artifactIds) => void remove(pendingDelete, artifactIds)}
          onClose={() => setPendingDelete(null)}
        />
      )}

      {lifecycle && (
        <AppDialog
          open
          onOpenChange={(open) =>
            !open && !lifecycleInFlightRef.current && setLifecycle(null)
          }
          showCloseButton={!lifecycleBusy}
          title={
            lifecycle.action === 'archive' ? 'Archive chat?' : 'Restore chat?'
          }
          description={
            lifecycle.action === 'archive'
              ? 'Archiving hides this chat from Recents and disables manual replies. Automations, Insights, and Secretary will continue to post updates.'
              : 'Restoring returns this chat to Recents and enables manual replies.'
          }
        >
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              disabled={lifecycleBusy}
              onClick={() => setLifecycle(null)}
            >
              Cancel
            </Button>
            <Button
              disabled={lifecycleBusy}
              onClick={() => void confirmLifecycle()}
            >
              {lifecycle.action === 'archive' ? 'Archive chat' : 'Restore chat'}
            </Button>
          </div>
        </AppDialog>
      )}
    </div>
  );
}

/**
 * One collapsible sidebar lane: a header that folds the lane away and the lane's chats, most recently
 * active first. Each lane keeps its own open state, so folding Public leaves Recents alone. Only
 * Recents splits into date buckets (Today / Yesterday / This week / Older) — it is the long history
 * that needs the signposts; Pinned and the shared lanes are short lists a second header would clutter.
 */
function ConversationLane({
  label,
  Icon,
  bucketByDate,
  items,
  emptyMessage,
  open: controlledOpen,
  onOpenChange,
  renderRow,
}: {
  label: string;
  /** The lane's glyph, shown ahead of its name. */
  Icon: typeof UsersIcon;
  bucketByDate: boolean;
  items: Conversation[];
  /** Shown instead of the buckets when the lane is empty (only Recents is rendered empty). */
  emptyMessage: string | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  renderRow: (conversation: Conversation) => JSX.Element;
}): JSX.Element {
  const [internalOpen, setInternalOpen] = useState(true);
  const open = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const groups = useMemo(
    () =>
      bucketByDate
        ? groupByDate(items)
        : [{ label: null, items: sortByActivity(items) }],
    [items, bucketByDate],
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="min-w-0">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="group flex h-8 max-md:h-11 w-full cursor-pointer items-center gap-1.5 rounded-[6px] px-[10px] py-0 text-left text-sidebar-section transition-colors duration-150 ease-out hover:bg-sidebar-accent"
        >
          <Icon
            className="size-3.5 shrink-0 text-inherit"
            strokeWidth={1.75}
            aria-hidden
          />
          <span className="text-caption max-md:text-compact font-normal uppercase tracking-wide text-sidebar-section">
            {label}
          </span>
          <ChevronDownIcon
            data-chevron
            className={cn(
              'size-[length:var(--sidebar-nav-icon)] shrink-0 text-inherit transition-transform duration-200 ease-out',
              open ? 'rotate-0' : '-rotate-90',
            )}
          />
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="flex flex-col gap-2 pt-0.5">
        {emptyMessage && (
          <p className="sidebar-fade px-[10px] pb-2 text-xs text-sidebar-section/70">
            {emptyMessage}
          </p>
        )}

        {groups.map((group) => (
          <section key={group.label ?? label} className="flex flex-col gap-1">
            {group.label && (
              <h3 className="px-[10px] pb-0.5 text-caption max-md:text-compact font-normal tracking-wide text-sidebar-section">
                {group.label}
              </h3>
            )}
            <div className="flex flex-col gap-0.5 max-md:gap-1">
              {group.items.map(renderRow)}
            </div>
          </section>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
}

interface ConversationRowProps {
  conversation: Conversation;
  active: boolean;
  onClick: () => void;
  archived?: boolean;
  onPin?: () => void;
  onRename?: (newTitle: string) => void;
  onDelete?: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
}

function ConversationRow({
  conversation,
  active,
  archived = false,
  onClick,
  onPin,
  onRename,
  onDelete,
  onArchive,
  onRestore,
}: ConversationRowProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  /**
   * The share dialog, which this row and the chat's own header both open — the same dialog, on the same
   * terms. It carries no workspace condition: in a personal workspace it drops its audience half and
   * offers the snapshot link alone, so gating the row on the kind of workspace only made one of the two
   * entry points disappear.
   */
  const [shareOpen, setShareOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const workspaceId = useUiStore((s) => s.selectedWorkspaceId);

  function startEditing(): void {
    if (!onRename) return;
    setDraft(conversation.title || 'Untitled');
    setEditing(true);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
  }

  function commitRename(): void {
    setEditing(false);
    onRename?.(draft);
  }

  function cancelRename(): void {
    setEditing(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelRename();
    }
  }

  const title = conversation.title || 'Untitled';

  return (
    <div
      className={cn(
        'group relative box-border flex h-8 max-md:h-11 max-md:gap-3 min-w-0 cursor-pointer items-center gap-1.5 rounded-[6px] max-md:rounded-lg px-[10px] transition-colors duration-150 ease-out',
        'py-0 sidebar-row-text leading-snug text-sidebar-foreground',
        active ? 'bg-sidebar-accent' : 'hover:bg-sidebar-accent',
      )}
    >
      {editing ? (
        <>
          {conversation.pinnedAt ? (
            <PinIcon className="size-[0.8125rem] max-md:size-4 shrink-0 text-inherit" />
          ) : (
            <MessageCircleIcon className="size-[0.8125rem] max-md:size-4 shrink-0 text-inherit" />
          )}
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={commitRename}
            className="h-[1.375rem] flex-1 rounded-sm border border-border bg-card px-1.5 sidebar-row-text text-foreground outline-none ring-1 ring-foreground/10"
          />
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={onClick}
            className={cn(
              'flex min-w-0 flex-1 items-center gap-1.5 truncate text-left text-inherit',
              sidebarRowStretchClass,
            )}
            title={title}
          >
            {conversation.pinnedAt ? (
              <PinIcon className="size-[0.8125rem] max-md:size-4 shrink-0 text-inherit" />
            ) : (
              <MessageCircleIcon className="size-[0.8125rem] max-md:size-4 shrink-0 text-inherit" />
            )}
            <span
              className={cn(
                'flex-1 truncate text-inherit',
                !active && conversation.unread ? 'font-semibold' : null,
              )}
            >
              {title}
            </span>
          </button>
          {!active && conversation.unread ? (
            <span
              role="status"
              className={sidebarUnreadDotClass}
              aria-label="Unread message"
            />
          ) : null}

          {(onPin || onRename || onDelete || onArchive || onRestore) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Actions for ${title}`}
                  className="relative z-10 size-[1.625rem] max-md:size-11 shrink-0 text-sidebar-foreground opacity-100 transition-opacity hover:bg-foreground/10 md:opacity-0 md:group-hover:opacity-100 data-[state=open]:opacity-100"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onRename && (
                  <DropdownMenuItem onClick={startEditing}>
                    <PencilIcon className="size-4" strokeWidth={1.75} /> Rename
                  </DropdownMenuItem>
                )}
                {onPin && (
                  <DropdownMenuItem onClick={onPin}>
                    <PinIcon className="size-4" strokeWidth={1.75} />{' '}
                    {conversation.pinnedAt ? 'Unpin' : 'Pin'}
                  </DropdownMenuItem>
                )}
                {!archived && onArchive && (
                  <DropdownMenuItem onClick={onArchive}>
                    <ArchiveIcon className="size-4" strokeWidth={1.75} />{' '}
                    Archive
                  </DropdownMenuItem>
                )}
                {archived && onRestore && (
                  <DropdownMenuItem onClick={onRestore}>
                    <RotateCcwIcon className="size-4" strokeWidth={1.75} />{' '}
                    Restore
                  </DropdownMenuItem>
                )}
                {!archived && onRename && (
                  <DropdownMenuItem onClick={() => setShareOpen(true)}>
                    <Share2Icon className="size-4" strokeWidth={1.75} /> Share
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <DropdownMenuItem variant="destructive" onClick={onDelete}>
                    <TrashIcon className="size-4" strokeWidth={1.75} /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {!archived && onRename && (
            <ConversationShareDialog
              conversationId={conversation.id}
              workspaceId={workspaceId}
              initialVisibility={
                (conversation.visibility ?? 'private') as ConversationVisibility
              }
              open={shareOpen}
              onOpenChange={setShareOpen}
            />
          )}
        </>
      )}
    </div>
  );
}

function ChatSkeleton({ count = 4 }: { count?: number }): JSX.Element {
  return (
    <div className="flex flex-col gap-[6px]">
      {Array.from({ length: count }).map((_, i) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <Skeleton key={i} className="h-8 w-full rounded-[6px]" />
      ))}
    </div>
  );
}

interface Group {
  /** The bucket header, or null for a lane that renders one unlabeled list (Pinned). */
  label: string | null;
  items: Conversation[];
}

/**
 * The stable activity timestamp the sidebar orders and buckets by. Uses `lastMessageAt` when present.
 * `null` (no messages yet) falls back to `createdAt` — not `updatedAt`, which mark-read bumps — so an
 * empty conversation doesn't jump to the top / into Today on open. `undefined` (field absent during a
 * rolling deploy) keeps the legacy `updatedAt` behavior.
 */
function activityIso(c: Conversation): string {
  return c.lastMessageAt !== undefined
    ? (c.lastMessageAt ?? c.createdAt)
    : (c.updatedAt ?? c.createdAt);
}

/** Most recently active first — the order every lane lists its chats in. */
function sortByActivity(input: ReadonlyArray<Conversation>): Conversation[] {
  return [...input].sort(
    (first, second) =>
      new Date(activityIso(second)).getTime() -
      new Date(activityIso(first)).getTime(),
  );
}

/**
 * Bucket the Recents lane's chats by how recent they are. Pinned chats never reach here — they have
 * their own lane — so this is a pure date split.
 */
function groupByDate(input: ReadonlyArray<Conversation>): Group[] {
  const today: Conversation[] = [];
  const yesterday: Conversation[] = [];
  const thisWeek: Conversation[] = [];
  const older: Conversation[] = [];

  const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

  for (const c of sortByActivity(input)) {
    const d = new Date(activityIso(c));
    if (isToday(d)) today.push(c);
    else if (isYesterday(d)) yesterday.push(c);
    else if (d.getTime() > oneWeekAgo) thisWeek.push(c);
    else older.push(c);
  }

  const groups: Group[] = [];
  if (today.length > 0) groups.push({ label: 'Today', items: today });
  if (yesterday.length > 0)
    groups.push({ label: 'Yesterday', items: yesterday });
  if (thisWeek.length > 0) groups.push({ label: 'This week', items: thisWeek });
  if (older.length > 0) groups.push({ label: 'Older', items: older });
  return groups;
}

function readSearchParam(search: unknown, key: string): string | null {
  if (typeof search === 'string') {
    const sp = new URLSearchParams(
      search.startsWith('?') ? search.slice(1) : search,
    );
    return sp.get(key);
  }
  if (search !== null && typeof search === 'object' && key in search) {
    const v = (search as Record<string, unknown>)[key];
    return typeof v === 'string' ? v : null;
  }
  return null;
}
